import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, X, CheckCircle2, Image as ImageIcon, Link2 } from "lucide-react";
import { toast } from "sonner";
import VideoTrimmer from "@/components/VideoTrimmer";

const PACKAGES = [
  { name: "Basic", price: "UGX 50,000", points: ["Platform listing", "Photos & location", "Buyer inquiries"] },
  { name: "Premium", price: "UGX 250,000", points: ["Featured placement", "Social & WhatsApp boost", "Agent follow-up"] },
  { name: "Corporate", price: "UGX 1,000,000+", points: ["Estates & farms", "Multi-listing", "Custom marketing"] },
];

export default function SellProperty() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ property_types: [], title_statuses: [], regions: [] });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    title: "", description: "",
    property_type: "", title_status: "", region: "", district: "", location: "",
    price_ugx: "", size: "", size_unit: "Acre",
    bedrooms: 0, bathrooms: 0,
    features: "",
    images: [],
    imageUrl: "",
    video_url: "",
    owner_name: "", owner_phone: "", owner_email: "",
    package: "Basic",
  });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleFile = (files) => {
    const list = Array.from(files);
    list.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (file.size > 1.5 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 1.5MB — please use smaller images or paste a URL.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => set("images", [...data.images, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const addUrl = () => {
    if (!data.imageUrl) return;
    set("images", [...data.images, data.imageUrl]);
    set("imageUrl", "");
  };

  const removeImg = (i) => set("images", data.images.filter((_, idx) => idx !== i));

  const validateStep1 = () =>
    data.title && data.property_type && data.title_status && data.region && data.district && data.location && data.price_ugx;

  const submit = async () => {
    if (!data.owner_name || !data.owner_phone) { toast.error("Owner name and phone are required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: data.title,
        description: data.description,
        property_type: data.property_type,
        title_status: data.title_status,
        region: data.region,
        district: data.district,
        location: data.location,
        price_ugx: parseInt(data.price_ugx || 0, 10),
        size: parseFloat(data.size || 0),
        size_unit: data.size_unit,
        bedrooms: parseInt(data.bedrooms || 0, 10),
        bathrooms: parseInt(data.bathrooms || 0, 10),
        images: data.images,
        features: data.features.split(",").map((f) => f.trim()).filter(Boolean),
        video_url: data.video_url || null,
        owner_name: data.owner_name,
        owner_phone: data.owner_phone,
        owner_email: data.owner_email,
        package: data.package,
      };
      const created = await api.post("/properties", payload);
      const propertyId = created.data.id;
      toast.success("Listing saved — redirecting to secure payment…");
      const checkout = await api.post("/payments/checkout", {
        package: data.package,
        origin_url: window.location.origin,
        property_id: propertyId,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit listing");
      setSubmitting(false);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="sell-page">
      <div className="text-center mb-10">
        <div className="eyebrow mb-3">List Your Property</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Reach 20,000+ buyers in days, not months.</h1>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Submit your land, home, farm, or commercial property. We verify, market, and connect you with qualified buyers.</p>
      </div>

      {/* Stepper */}
      {step < 4 && (
        <div className="flex items-center justify-center gap-3 mb-10" data-testid="sell-stepper">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full grid place-items-center font-display font-bold text-sm ${s === step ? "bg-primary text-primary-foreground" : s < step ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"}`}>
                {s < step ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-px ${s < step ? "bg-secondary" : "bg-border"}`} />}
            </div>
          ))}
        </div>
      )}

      <div className="bg-card border border-border rounded-3xl p-8 md:p-10">
        {step === 1 && (
          <div className="space-y-5" data-testid="sell-step-1">
            <h2 className="font-display text-2xl">1. Property details</h2>
            <Field label="Listing title">
              <Input value={data.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g., 1 Acre Lake View Land — Mukono" data-testid="field-title" />
            </Field>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Property type">
                <Select value={data.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="field-type"><SelectValue placeholder="Choose type" /></SelectTrigger>
                  <SelectContent>{meta.property_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Title status">
                <Select value={data.title_status} onValueChange={(v) => set("title_status", v)}>
                  <SelectTrigger data-testid="field-title-status"><SelectValue placeholder="Choose title" /></SelectTrigger>
                  <SelectContent>{meta.title_statuses?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Region">
                <Select value={data.region} onValueChange={(v) => set("region", v)}>
                  <SelectTrigger data-testid="field-region"><SelectValue placeholder="Region" /></SelectTrigger>
                  <SelectContent>{meta.regions?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="District">
                <Input value={data.district} onChange={(e) => set("district", e.target.value)} placeholder="Mukono" data-testid="field-district" />
              </Field>
              <Field label="Specific location">
                <Input value={data.location} onChange={(e) => set("location", e.target.value)} placeholder="Ngombere, Katosi Rd" data-testid="field-location" />
              </Field>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Asking price (UGX)">
                <Input type="number" value={data.price_ugx} onChange={(e) => set("price_ugx", e.target.value)} placeholder="40000000" data-testid="field-price" />
              </Field>
              <Field label="Size">
                <Input type="number" value={data.size} onChange={(e) => set("size", e.target.value)} placeholder="1" data-testid="field-size" />
              </Field>
              <Field label="Unit">
                <Select value={data.size_unit} onValueChange={(v) => set("size_unit", v)}>
                  <SelectTrigger data-testid="field-size-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Acre">Acre</SelectItem>
                    <SelectItem value="Decimal">Decimal</SelectItem>
                    <SelectItem value="sqm">sqm</SelectItem>
                    <SelectItem value="Hectare">Hectare</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Description">
              <Textarea rows={4} value={data.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell buyers what makes this property special…" data-testid="field-description" />
            </Field>
            <Field label="Features (comma-separated)">
              <Input value={data.features} onChange={(e) => set("features", e.target.value)} placeholder="Lake view, Tarmac access, Borehole" data-testid="field-features" />
            </Field>

            <div className="flex justify-end pt-3">
              <Button
                onClick={() => validateStep1() ? setStep(2) : toast.error("Please fill all required fields")}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="step-1-next"
              >
                Next: Photos
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="sell-step-2">
            <h2 className="font-display text-2xl">2. Photos</h2>
            <p className="text-sm text-muted-foreground">Add at least one. Upload from your device or paste image URLs.</p>

            <label className="block border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:bg-muted/40 transition" data-testid="upload-zone">
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFile(e.target.files)} data-testid="upload-input" />
              <Upload className="w-6 h-6 mx-auto text-primary" />
              <div className="mt-3 font-display text-lg">Click to upload photos</div>
              <div className="text-xs text-muted-foreground mt-1">JPG/PNG up to 1.5MB each</div>
            </label>

            <div className="flex gap-2">
              <Input value={data.imageUrl} onChange={(e) => set("imageUrl", e.target.value)} placeholder="…or paste image URL" data-testid="field-img-url" />
              <Button type="button" onClick={addUrl} variant="outline" className="rounded-full border-border whitespace-nowrap" data-testid="add-url-btn">
                <Link2 className="w-4 h-4" /> Add URL
              </Button>
            </div>

            {data.images.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3" data-testid="image-gallery">
                {data.images.map((src, i) => (
                  <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-border">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeImg(i)} className="absolute top-1.5 right-1.5 bg-background/90 p-1 rounded-full" data-testid={`remove-img-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {data.images.length === 0 && (
              <div className="bg-muted/40 rounded-2xl p-6 text-center text-sm text-muted-foreground">
                <ImageIcon className="w-5 h-5 mx-auto mb-2" /> No photos added yet
              </div>
            )}

            <div className="pt-4 border-t border-border">
              <Label className="text-xs font-medium text-foreground/80">30-second property reel (optional)</Label>
              <div className="mt-3">
                <VideoTrimmer value={data.video_url} onChange={(v) => set("video_url", v)} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">Upload a raw video — we trim it to 30 seconds and compress it in your browser (nothing leaves your device until you submit). Listings with a reel convert 3× better.</p>
            </div>

            <div className="flex justify-between pt-3">
              <Button onClick={() => setStep(1)} variant="outline" className="rounded-full border-border" data-testid="step-2-back">Back</Button>
              <Button onClick={() => setStep(3)} className="rounded-full bg-primary hover:bg-primary/90" data-testid="step-2-next">Next: Package</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="sell-step-3">
            <h2 className="font-display text-2xl">3. Choose package & confirm</h2>

            <div className="grid md:grid-cols-3 gap-4">
              {PACKAGES.map((pkg) => (
                <button
                  key={pkg.name}
                  type="button"
                  onClick={() => set("package", pkg.name)}
                  className={`text-left rounded-2xl border p-5 transition-all ${
                    data.package === pkg.name ? "border-primary ring-2 ring-primary/30 bg-card" : "border-border bg-card hover:border-primary/50"
                  }`}
                  data-testid={`pkg-select-${pkg.name.toLowerCase()}`}
                >
                  <div className="eyebrow">{pkg.name}</div>
                  <div className="font-display text-2xl font-bold mt-2">{pkg.price}</div>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {pkg.points.map((pt) => <li key={pt} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-primary mt-1 shrink-0" /> {pt}</li>)}
                  </ul>
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-4 pt-2">
              <Field label="Your name">
                <Input value={data.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="field-owner-name" />
              </Field>
              <Field label="Phone / WhatsApp">
                <Input value={data.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} data-testid="field-owner-phone" />
              </Field>
              <Field label="Email (optional)">
                <Input type="email" value={data.owner_email} onChange={(e) => set("owner_email", e.target.value)} data-testid="field-owner-email" />
              </Field>
            </div>

            <div className="bg-muted/40 rounded-2xl p-5 text-sm text-muted-foreground">
              Submitting creates a <b className="text-foreground">pending</b> listing and takes you to <b className="text-foreground">Stripe secure checkout</b> to pay the {data.package} package fee. Test card: <span className="font-mono">4242 4242 4242 4242</span> · any future date · any CVC.
            </div>

            <div className="flex justify-between pt-3">
              <Button onClick={() => setStep(2)} variant="outline" className="rounded-full border-border" data-testid="step-3-back">Back</Button>
              <Button onClick={submit} disabled={submitting} className="rounded-full bg-primary hover:bg-primary/90" data-testid="sell-submit">
                {submitting ? "Redirecting to checkout…" : `Submit & pay ${data.package}`}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-10" data-testid="sell-success">
            <div className="w-16 h-16 rounded-full bg-primary/10 grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-display text-3xl">Submitted!</h2>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
              Thanks — your property is pending review. Our team will call or WhatsApp you within 24 hours to verify documents and confirm package payment.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button onClick={() => navigate("/")} variant="outline" className="rounded-full border-border" data-testid="success-home">Home</Button>
              <Button onClick={() => { setStep(1); setData({ ...data, title: "", description: "", images: [] }); }} className="rounded-full bg-primary hover:bg-primary/90" data-testid="success-another">List another</Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-foreground/80">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
